import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import { useEffect, useState } from "react";
import Spinner from "../../components/ui/Spinner";
import {
    API_BASE_URL,
    API_URL,
    DEFAULT_PAGE_DESCRIPTION,
    DEFAULT_PAGE_TITLE,
    DEFAULT_PER_PAGE,
} from "../../utilities/constants";
import { NavLink, useLocation, useNavigate, useParams } from "react-router";
import useQueryParams from "../../hooks/useQueryParams";
import { handleApiCall, jsonToQueryParams } from "../../utilities/helper";
import Pagination from "../../components/ui/Pagination";
import { FaCheckCircle, FaClock, FaTimesCircle } from "react-icons/fa";


const TemplateHistoryDetails = () => {
    const [loading, setLoading] = useState(true);
    const [categoryLoading, setCategoryLoading] = useState(false);
    const location = useLocation();
    const categoryId = location.state?.categoryId ? location.state?.categoryId : null;
    const { id } = useParams();

    const [categories, setCategories] = useState([]);
    const { queryParams } = useQueryParams();
    const pageNo = queryParams.get("pageNo") || 0;
    const pageSize = queryParams.get("pageSize") || DEFAULT_PER_PAGE;
    const sortOrder = queryParams.get("sortOrder") || "DESC";
    const [controller, setController] = useState(null);

    const [categorySearch, setCategorySearch] = useState(queryParams.get("categorySearch") || "");
    const categoryQuery = jsonToQueryParams({ search: categorySearch });


    const callListApi = async () => {
        setCategoryLoading(true);
        try {
            const response = await handleApiCall(
                `${API_BASE_URL}/api/1.0.0/notification/${id}?pageNo=${pageNo}&pageSize=${pageSize}&sortBy=createdAt&direction=${sortOrder}&${categoryQuery}`,
                { method: "GET" }
            );
            if (response.responseStatus == true) {
                setCategories(response.data);
            }
            else {
                setCategories(null);
            }
        } catch (e) {
            console.error("Error", e);
        } finally {
            setCategoryLoading(false);
        }
    };

    useEffect(() => {
        callListApi();
    }, []);

    const templateData = categories?.list?.[0];

    return (
        <>
            <PageMeta title={`Catalog - ${DEFAULT_PAGE_TITLE}`} description={`Catalog - ${DEFAULT_PAGE_DESCRIPTION}`} />
            <div className="flex">
                 <PageBreadcrumb
                    pageTitle="Notification Details"
                    breadcrumbs={[
                        { label: "Notification", path: "/notification" },
                        { label: "Notification Details" }
                    ]}
                />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                <div className="col-span-12 md:col-span-6 bg-gray-50 dark:bg-gray-800 p-6 rounded-xl shadow-md">
                    {categoryLoading ? (
                        <div className="flex justify-center items-center">
                            <Spinner />
                        </div>
                    ) : (
                        <div className="relative sm:rounded-lg max-h-[70vh] overflow-y-auto">
                            <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400 rounded-lg">
                                <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-400">
                                    <tr>
                                        <th scope="col" className="px-6 py-3">Name</th>
                                        <th scope="col" className="px-6 py-3">Email</th>
                                        <th scope="col" className="px-6 py-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {categories?.list?.map((user, index) => (
                                        <tr
                                            key={index}
                                            className="border-b dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300"
                                        >
                                            <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                                                <span>{user?.name || "-"}</span>
                                            </td>
                                            <td className="px-6 py-4">{user?.email}</td>
                                            <td className="px-6 py-4">
                                                <div
                                                    className={`flex items-center gap-2 text-xs px-3 py-1 rounded-full w-fit
                                                            ${user?.status === 1
                                                            ? 'bg-green-100 text-green-700'
                                                            : user?.status === 2
                                                                ? 'bg-red-100 text-red-700'
                                                                : 'bg-yellow-100 text-yellow-700'}`}
                                                >
                                                    {user?.status === 1 && <FaCheckCircle className="text-green-500" />}
                                                    {user?.status === 2 && <FaTimesCircle className="text-red-500" />}
                                                    {user?.status === 3 && <FaClock className="text-yellow-600" />}
                                                    {user?.status === 1 ? "Active" : "Inactive"}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <div className="flex justify-center items-center mt-4">
                                <Pagination
                                    pageParam="pageNo"
                                    currentPage={categories?.pageNo + 1}
                                    totalElements={categories?.totalRecords}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="col-span-12 md:col-span-6 bg-white dark:bg-gray-900 p-6 rounded-xl shadow-md">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                        Template Details
                    </h2>

                    {templateData ? (
                        <div className="space-y-4">
                            <div>
                                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Template Name</p>
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                    {templateData?.templateName || "-"}
                                </p>
                            </div>

                            <div>
                                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Subject</p>
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                    {templateData?.templateSubject || "-"}
                                </p>
                            </div>

                            <div>
                                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Body</p>
                                <p className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-line bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                    {templateData?.templateBody || "-"}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No template details available</p>
                    )}
                </div>
            </div>



        </>
    );

};

export default TemplateHistoryDetails;
